import { SelectOption, OptionGroupProps } from '@ott/autocomplete';

import { SUGGESTION_TYPES } from './helpers/prepareSuggestions';

export enum StationType {
  Airport = 'airport',
  RailwayStation = 'railway_station',
  BusTerminal = 'bus_terminal',
  BusStation = 'bus_station',
  MetroStation = 'metro_station',
  Mall = 'mall',
}

export type ServerGeoPoint = {
  country: string;
  district: string | null;
  geopointId: number;
  lat: number;
  lon: number;
  name: string;
  region: string | null;
  trnslt: string;
};

export type ServerStation = {
  id: number;
  name: string;
  geopoint: {
    id: ServerGeoPoint['geopointId'];
    name: ServerGeoPoint['name'];
    trnslt: ServerGeoPoint['trnslt'];
  };
  districtName?: string;
  regionName?: string;
  countryName: string;
  trnslt: string;
  typeName: StationType;
};

export type ServerError = {
  code: number;
  eventId: number;
  message: string;
};

export enum TargetType {
  Station = 'station',
  GeoPoint = 'geopoint',
}

export type ServerSuggestion = {
  country: ServerGeoPoint['country'];
  district: ServerGeoPoint['district'];
  geopoint: {
    id: ServerGeoPoint['geopointId'];
    name: ServerGeoPoint['name'];
  };
  name: ServerGeoPoint['name'];
  region: ServerGeoPoint['region'];
  trnslt: ServerGeoPoint['trnslt'];
  type: typeof SUGGESTION_TYPES[keyof typeof SUGGESTION_TYPES] | '';
  target: {
    id: number;
    type: TargetType;
    entity: {
      name: string;
      typeName?: StationType;
      trnslt: string;
      iata?: string;
    };
  };
};

export type GeoPoint = {
  country: ServerGeoPoint['country'];
  district: ServerGeoPoint['district'];
  id: ServerGeoPoint['geopointId'];
  name: ServerGeoPoint['name'];
  region: ServerGeoPoint['region'];
  translit: ServerGeoPoint['trnslt'];
};

export type Station = {
  countryName: ServerStation['countryName'];
  districtName: Required<ServerStation>['districtName'] | null;
  geoPointId: ServerStation['geopoint']['id'];
  geoPointName: ServerStation['geopoint']['name'];
  geoPointTranslit: ServerStation['geopoint']['trnslt'];
  name: ServerStation['name'];
  regionName: Required<ServerStation>['regionName'] | null;
  id: ServerStation['id'];
  targetType: TargetType.Station;
  translit: ServerStation['trnslt'];
  typeName: StationType;
};

export type GeoAutocompleteOption = SelectOption & {
  country: GeoPoint['country'] | Station['countryName'];
  district: GeoPoint['district'] | Station['districtName'];
  region: GeoPoint['region'] | Station['regionName'];
  translit: GeoPoint['translit'] | Station['translit'];
  type?: ServerSuggestion['type'];
  targetType: TargetType;
  entityType?: StationType;
  geoPoint: GeoPoint;
};

export type SearchForm<DateType = Date> = {
  departure: string;
  departureOption: GeoAutocompleteOption;
  destination: string;
  destinationOption: GeoAutocompleteOption;
  date: DateType;
  adults: number;
  childrenAges: number[];
};

export type Suggestion = GeoAutocompleteOption | OptionGroupProps;
export const checkIfSuggestionIsGeoAutocompleteOption = (
  suggestion: Suggestion
): suggestion is GeoAutocompleteOption => {
  return (suggestion as GeoAutocompleteOption).geoPoint !== undefined;
};
